'use client';

import { usePathname } from 'next/navigation';
import AuthenticatedLayout from './AuthenticatedLayout';

interface ConditionalLayoutProps {
  children: React.ReactNode;
}

export default function ConditionalLayout({
  children,
}: ConditionalLayoutProps) {
  const pathname = usePathname();

  // Define auth routes that should NOT have the authenticated layout
  const authRoutes = [
    '/login',
    '/signup',
    '/forgot-password',
    '/reset-password',
  ];

  // Define API routes that should not have layout
  const apiRoutes = ['/api'];

  // Define routes that SHOULD have the authenticated layout
  const authenticatedRoutes = [
    '/admin',
    '/dashboard',
    '/student-management',
    '/settings',
    '/reports',
    '/transportation',
    '/profile',
    '/student',
    '/help-and-support',
    '/attendance',
    '/fees',
    '/team',
    '/grades',
    '/admissions',
    '/notices',
    '/suggestions-complaints',
    '/assessment',
  ];

  // Check if it's an auth route
  const isAuthRoute = authRoutes.some((route) => pathname?.startsWith(route));

  // Check if it's an API route
  const isApiRoute = apiRoutes.some((route) => pathname?.startsWith(route));

  // Check if it's a route that should have authenticated layout
  const shouldHaveAuthLayout = authenticatedRoutes.some((route) =>
    pathname?.startsWith(route),
  );

  // For auth pages, API routes, and home page - no layout
  if (isAuthRoute || isApiRoute || pathname === '/') {
    return <>{children}</>;
  }

  // For authenticated routes - wrap with AuthenticatedLayout
  if (shouldHaveAuthLayout) {
    return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
  }

  // For any other routes - default to no layout (fallback)
  return <>{children}</>;
}
